import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  IncentiveListItem,
  IncentiveListResponse,
  IncentiveSavePayload,
  Incentive,
  IncentiveDepartmentLookup,
  IncentiveEmployeeLookup,
} from '@/api/types/incentive.types'
import type { ApiResponse } from '@/api/types'

/**
 * Incentive (Payroll) service
 */
export class IncentiveService {
  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<IncentiveListResponse> {
    const url = endpoints.incentive.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<
      ApiResponse<IncentiveListResponse> | IncentiveListResponse | any
    >(url)
    const payload = response.data

    const mapItem = (item: any): IncentiveListItem => ({
      id: item.id ?? 0,
      employeeId: item.employeeId ?? item.empId ?? item.EmpId ?? item.empid,
      employeeName: item.employeeName ?? item.empName ?? item.employeeNameEn ?? '',
      departmentId: item.departmentId,
      departmentName: item.departmentName ?? '',
      monthYear: item.monthYear ?? '',
      amount: item.amount ?? 0,
      debitAccount: item.debitAccount ?? item.debitAccountId,
      debitAccountName: item.debitAccountName ?? '',
      details: item.details ?? '',
      createdDate: item.createdDate ?? item.createdOn ?? '',
    })

    if (payload && typeof payload === 'object') {
      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data.map(mapItem),
        }
      }
      if (Array.isArray(payload)) {
        return {
          totalRecords: payload.length,
          pageNumber,
          pageSize,
          data: payload.map(mapItem),
        }
      }
    }

    return {
      totalRecords: 0,
      pageNumber,
      pageSize,
      data: [],
    }
  }

  async getById(id: number, _branchId?: number): Promise<Incentive> {
    const url = endpoints.incentive.getById(id)
    const response = await apiClient.get<ApiResponse<Incentive> | Incentive | any>(url)
    let data = response.data
    if (data && 'data' in data && data.data != null) data = data.data
    return {
      id: data?.id ?? id,
      branchId: data?.branchId ?? _branchId ?? 0,
      employeeId: data?.employeeId ?? data?.empId ?? 0,
      departmentId: data?.departmentId ?? 0,
      monthYear: data?.monthYear ?? '',
      debitAccount: data?.debitAccount ?? data?.debitAccountId ?? 0,
      amount: data?.amount ?? 0,
      details: data?.details ?? '',
    }
  }

  async getDepartments(branchId: number): Promise<IncentiveDepartmentLookup[]> {
    const url = endpoints.incentive.getDepartments(branchId)
    const response = await apiClient.get<
      ApiResponse<IncentiveDepartmentLookup[]> | IncentiveDepartmentLookup[] | any
    >(url)
    const payload = response.data
    const list = Array.isArray(payload)
      ? payload
      : payload?.data && Array.isArray(payload.data)
        ? payload.data
        : []
    return list.map((d: any) => ({
      id: d.id ?? d.departmentId ?? 0,
      lookupNameEn: d.lookupNameEn ?? d.nameEn ?? d.name ?? '',
      lookupNameAr: d.lookupNameAr ?? d.nameAr ?? '',
      name: d.name ?? d.lookupNameEn ?? d.lookupNameAr ?? '',
    }))
  }

  async getEmployees(branchId: number): Promise<IncentiveEmployeeLookup[]> {
    const url = endpoints.incentive.getEmployees(branchId)
    const response = await apiClient.get<
      ApiResponse<IncentiveEmployeeLookup[]> | IncentiveEmployeeLookup[] | any
    >(url)
    const payload = response.data
    const list = Array.isArray(payload)
      ? payload
      : payload?.data && Array.isArray(payload.data)
        ? payload.data
        : []
    return list.map((e: any) => ({
      id: e.id ?? e.empId ?? e.employeeId ?? 0,
      empCode: e.empCode ?? e.code ?? '',
      empFirstNameEn: e.empFirstNameEn ?? e.firstName ?? e.firstNameEn ?? '',
      empFamilyNameEn: e.empFamilyNameEn ?? e.familyName ?? e.lastName ?? '',
      empDepartmetId: e.empDepartmetId ?? e.departmentId ?? e.empDepartmentId ?? 0,
      departmentId: e.departmentId ?? e.empDepartmetId ?? 0,
    }))
  }

  async save(payload: IncentiveSavePayload): Promise<Incentive> {
    const url = endpoints.incentive.post
    const response = await apiClient.post<ApiResponse<Incentive> | Incentive>(url, payload)
    const data = response.data
    const result = data && 'data' in data ? (data as ApiResponse<Incentive>).data : data
    return result as Incentive
  }

  async update(payload: IncentiveSavePayload): Promise<Incentive> {
    const url = endpoints.incentive.put
    const response = await apiClient.put<ApiResponse<Incentive> | Incentive>(url, payload)
    const data = response.data
    const result = data && 'data' in data ? (data as ApiResponse<Incentive>).data : data
    return result as Incentive
  }

  async delete(id: number): Promise<void> {
    const url = endpoints.incentive.delete(id)
    await apiClient.delete(url)
  }
}

export const incentiveService = new IncentiveService()
